import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../../lib/api";
import { QuestionTypeBadge, StatusBadge } from "../../components/StatusBadge";
import { ChevronRight, ArrowLeft, X, Lightbulb, CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function QuizScreen() {
  const { t, i18n } = useTranslation();
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [answers, setAnswers] = useState({});
  const [startedAt] = useState(() => new Date().toISOString());
  const [finalizing, setFinalizing] = useState(false);
  const [finalScore, setFinalScore] = useState(null);

  const lang = i18n.resolvedLanguage || "en";

  useEffect(() => {
    api.get(`/student/quiz/${moduleId}?lang=${lang}`).then((r) => setData(r.data));
    // Re-fetch when language changes, only if we haven't started answering yet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId, lang]);

  useEffect(() => {
    const tm = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(tm);
  }, []);

  const current = data?.questions?.[idx];
  const total = data?.questions?.length || 0;
  const progress = total ? Math.round(((idx + (feedback ? 1 : 0)) / total) * 100) : 0;

  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const submit = async () => {
    if (selected == null) return;
    setSubmitting(true);
    try {
      const { data: res } = await api.post("/student/quiz/submit-answer", {
        module_id: moduleId,
        question_id: current.id,
        selected_index: selected,
        lang,
      });
      setFeedback(res);
      setAnswers((prev) => ({
        ...prev,
        [current.id]: { selected_index: selected, time_taken_seconds: seconds },
      }));
    } finally { setSubmitting(false); }
  };

  const finalizeAttempt = async (collected) => {
    setFinalizing(true);
    try {
      const payload = {
        module_id: moduleId,
        started_at: startedAt,
        ended_at: new Date().toISOString(),
        answers: Object.entries(collected).map(([qid, a]) => ({
          question_id: qid,
          selected_index: a.selected_index,
          time_taken_seconds: a.time_taken_seconds,
        })),
      };
      const { data: res } = await api.post("/quiz/submit", payload);
      setFinalScore(res);
    } catch (e) {
      navigate("/student/quiz");
    } finally { setFinalizing(false); }
  };

  const nextQ = () => {
    if (idx + 1 >= total) {
      finalizeAttempt(answers);
      return;
    }
    setIdx((i) => i + 1);
    setSelected(null);
    setFeedback(null);
  };

  if (!data) return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <Loader2 className="animate-spin text-indigo-600" />
    </div>
  );

  const m = data.module;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <nav className="flex items-center gap-1.5 text-sm">
            <Link to="/student/quiz" className="text-slate-500 hover:text-slate-700">{t("student.quiz_screen.breadcrumb")}</Link>
            <ChevronRight size={14} className="text-slate-400" />
            <span className="font-semibold text-slate-900">{m.name}</span>
          </nav>
          <button onClick={() => setExitOpen(true)} data-testid="exit-quiz-btn" className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-100">
            <X size={14} /> {t("student.quiz_screen.exit_quiz")}
          </button>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl gap-6 px-6 py-8 lg:grid-cols-[1fr_320px]">
        {finalScore ? (
          <section className="rounded-2xl border border-slate-100 bg-white p-10 text-center shadow-card lg:col-span-2" data-testid="quiz-complete">
            <div className={`mx-auto flex h-20 w-20 items-center justify-center rounded-full ${finalScore.score_pct >= 75 ? "bg-emerald-100 text-emerald-600" : finalScore.score_pct >= 50 ? "bg-amber-100 text-amber-600" : "bg-rose-100 text-rose-600"}`}>
              <CheckCircle2 size={44} />
            </div>
            <h2 className="mt-5 text-3xl font-extrabold tracking-tight text-slate-900">{t("student.quiz_screen.quiz_complete")}</h2>
            <p className="mt-1 text-slate-500">{t("student.quiz_screen.quiz_complete_sub")}</p>
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              <div className="rounded-2xl bg-slate-50 p-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("student.quiz_screen.score")}</p>
                <p className="mt-1 text-3xl font-extrabold text-slate-900">{finalScore.score_pct}%</p>
                <p className="text-xs text-slate-500">{t("student.quiz_screen.correct_count", { correct: finalScore.correct_count, total: finalScore.total_questions })}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("student.quiz_screen.best_so_far")}</p>
                <p className="mt-1 text-3xl font-extrabold text-slate-900">{finalScore.best_score_pct}%</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("student.quiz_screen.time_taken")}</p>
                <p className="mt-1 text-3xl font-extrabold text-slate-900">{formatTime(seconds)}</p>
              </div>
            </div>
            <div className="mt-6 grid gap-2 sm:grid-cols-3">
              {Object.entries(finalScore.breakdown || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-2.5 text-sm">
                  <span className="capitalize text-slate-600">{t(`types.${k.charAt(0).toUpperCase() + k.slice(1)}`, { defaultValue: k })}</span>
                  <StatusBadge value={v} withDot compact />
                </div>
              ))}
            </div>
            <div className="mt-8 flex justify-center gap-3">
              <button onClick={() => window.location.reload()} data-testid="quiz-retry" className="rounded-xl border border-indigo-200 bg-white px-5 py-2.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-50">
                {t("student.quiz_screen.try_another")}
              </button>
              <button onClick={() => navigate("/student/quiz")} data-testid="quiz-back-to-list" className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700">
                {t("student.quiz_screen.back_to_quizzes")}
              </button>
            </div>
          </section>
        ) : (
        <>
        <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-card sm:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-700">{t("student.quiz_screen.question_of", { idx: idx + 1, total })}</p>
              <div className="mt-2 h-1.5 w-64 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-semibold tabular-nums text-slate-700">⏱ {formatTime(seconds)}</span>
              {data.questions?.[0]?.source === "ai" && (
                <span className="rounded-full bg-violet-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-violet-700" data-testid="ai-questions-badge">{t("student.quiz_screen.ai_badge")}</span>
              )}
              <button className="text-sm font-semibold text-indigo-600 hover:underline" data-testid="view-progress-btn">{t("student.quiz_screen.view_progress")}</button>
            </div>
          </div>

          <div className="mt-6">
            <QuestionTypeBadge type={current.type} />
            <h2 className="mt-4 text-2xl font-bold text-slate-900">{current.question}</h2>

            <div className="mt-6 space-y-3">
              {current.options.map((opt, i) => {
                const isSelected = selected === i;
                const isCorrect = feedback && i === feedback.correct_index;
                const isWrong = feedback && i === selected && !feedback.correct;
                let style = "border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/40";
                if (feedback) {
                  if (isCorrect) style = "border-emerald-500 bg-emerald-50 text-emerald-900";
                  else if (isWrong) style = "border-rose-500 bg-rose-50 text-rose-900";
                  else style = "border-slate-200 bg-slate-50 text-slate-500";
                } else if (isSelected) {
                  style = "border-indigo-500 bg-indigo-50";
                }
                return (
                  <button
                    key={i}
                    disabled={!!feedback}
                    onClick={() => setSelected(i)}
                    data-testid={`quiz-option-${i}`}
                    className={`flex w-full items-center gap-3 rounded-xl border-2 px-4 py-3.5 text-left text-sm font-medium transition-all ${style}`}
                  >
                    <span className={`flex h-7 w-7 flex-none items-center justify-center rounded-full border-2 text-xs font-bold ${
                      feedback && isCorrect ? "border-emerald-500 bg-emerald-500 text-white" :
                      feedback && isWrong ? "border-rose-500 bg-rose-500 text-white" :
                      isSelected ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-300 text-slate-500"
                    }`}>
                      {String.fromCharCode(65 + i)}
                    </span>
                    <span className="flex-1">{opt}</span>
                    {feedback && isCorrect && <CheckCircle2 size={18} className="text-emerald-600" />}
                    {feedback && isWrong && <XCircle size={18} className="text-rose-600" />}
                  </button>
                );
              })}
            </div>

            {feedback && (
              <div className={`mt-6 rounded-2xl border p-5 ${feedback.correct ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`} data-testid="quiz-feedback">
                <div className="flex items-center gap-2">
                  {feedback.correct ? <CheckCircle2 className="text-emerald-600" /> : <XCircle className="text-rose-600" />}
                  <h3 className={`text-lg font-bold ${feedback.correct ? "text-emerald-700" : "text-rose-700"}`}>
                    {feedback.correct ? t("student.quiz_screen.correct") : t("student.quiz_screen.incorrect")}
                  </h3>
                </div>
                <p className="mt-2 text-sm text-slate-700">{feedback.explanation}</p>
              </div>
            )}

            <div className="mt-6 flex items-center justify-between">
              <button
                disabled={idx === 0}
                onClick={() => { setIdx((i) => Math.max(0, i - 1)); setSelected(null); setFeedback(null); }}
                data-testid="quiz-previous-btn"
                className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <ArrowLeft size={14} /> {t("common.previous")}
              </button>
              {!feedback ? (
                <button
                  disabled={selected == null || submitting}
                  onClick={submit}
                  data-testid="quiz-submit-btn"
                  className="rounded-xl bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-60"
                >
                  {submitting ? t("student.quiz_screen.submitting") : t("common.submit")}
                </button>
              ) : (
                <button
                  onClick={nextQ}
                  disabled={finalizing}
                  data-testid="quiz-next-btn"
                  className="rounded-xl bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-60"
                >
                  {finalizing ? t("student.quiz_screen.finalizing") : idx + 1 >= total ? t("student.quiz_screen.finish_quiz") : t("student.quiz_screen.next_question")}
                </button>
              )}
            </div>
          </div>
        </section>

        <aside className="space-y-4">
          <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card">
            <h3 className="text-sm font-bold text-slate-900">{t("student.quiz_screen.about_quiz")}</h3>
            <div className="mt-3 space-y-2 text-sm">
              <p><span className="text-slate-500">{t("student.quiz_screen.module")}:</span> <span className="font-semibold text-slate-900">{m.name}</span></p>
              <p><span className="text-slate-500">{t("student.quiz_screen.subject")}:</span> <span className="font-semibold text-slate-900">{t(`subjects.${m.subject}`, { defaultValue: m.subject })}</span></p>
            </div>
            <div className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("student.quiz_screen.quiz_performance")}</p>
              <div className="mt-2 space-y-1.5 text-xs">
                <PerfRow label={t("types.Conceptual")} value={data.performance.conceptual} />
                <PerfRow label={t("types.Applied")} value={data.performance.applied} />
                <PerfRow label={t("types.Theoretical")} value={data.performance.theoretical} />
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-violet-200 bg-violet-50 p-5 text-sm text-violet-900">
            <div className="flex items-center gap-2">
              <Lightbulb size={16} /> <p className="font-semibold">{t("student.quiz_screen.tip")}</p>
            </div>
            <p className="mt-2 text-xs leading-relaxed">{t("student.quiz_screen.tip_body")}</p>
          </div>
        </aside>
        </>
        )}
      </main>

      {exitOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm" data-testid="exit-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-slate-900">{t("student.quiz_screen.exit_title")}</h3>
            <p className="mt-2 text-sm text-slate-600">{t("student.quiz_screen.exit_body")}</p>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setExitOpen(false)} data-testid="exit-cancel" className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">{t("common.cancel")}</button>
              <button onClick={() => navigate("/student/quiz")} data-testid="exit-confirm" className="rounded-xl bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-rose-700">{t("student.quiz_screen.exit_confirm")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const PerfRow = ({ label, value }) => (
  <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
    <span className="text-slate-600">{label}</span>
    <StatusBadge value={value} withDot compact />
  </div>
);
